/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { object, func } from 'prop-types';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import ReactMarkdown from 'react-markdown';
import Button from '@material-ui/core/Button';
import { trackEvent as track } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const useStyles = makeStyles((theme) => ({
    sectionHeader: {
        fontSize: '22px',
    },
    couponArea: {
        marginBottom: '25px',
    },
    noCoupons: {
        textAlign: 'center',
        fontSize: '22px',
        margin: '30px auto',
    },
    couponSingle: (panelBackgroundColor) => ({
        border: `2px dashed ${theme?.palette?.cms?.dimSilver || '#ccc'}`,
        padding: '15px 10px',
        boxSizing: 'border-box',
        overflow: 'hidden',
        maxWidth: '980px',
        width: '100%',
        textAlign: 'center',
        transition: 'all 0.3s',
        position: 'relative',
        backgroundColor: panelBackgroundColor?.color || theme?.palette?.cms?.['Light Beige'] || '#F8F8F0',
        minHeight: '145px',
    }),

    iconAndHeadingContainer: {
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center',
    },
    icon: {
        height: '60px',
    },
    headingContainer: {
        marginLeft: '10px',
    },
    couponHeading: {
        display: 'flex',
        justifyContent: 'center',
        '& h3': {
            fontSize: '26px',
            marginTop: '0',
            marginBottom: '0',
            position: 'relative',
            bottom: '5px',
            lineHeight: '1.3',
        },
        '& strong': {
            color: theme?.palette?.cms?.['CTA Red'] || '#A1001A',
        },
    },
    copyContainer: {
        margin: '0 20px',
    },
    promoCopy: {
        '& p': {
            marginTop: '5px',
            fontSize: '18px',
        },
        '& strong': {
            color: theme?.palette?.cms?.['CTA Red'] || '#A1001A',
        },
    },
    detailsLink: {
        paddingTop: '5px',
        textDecoration: 'underline',
        display: 'inline-block',
        color: theme?.palette?.cms?.tertiary_hover || '#666',
        cursor: 'pointer',
        minWidth: '135px',
        padding: '3px',
        transition: 'all .3',
        marginBottom: '10px',
        '&:focus': {
            outline: 'none',
        },
    },
    deets_filler: {
        marginTop: '20px',
    },
    ctaBtn: {
        backgroundColor: theme?.palette?.cms?.['CTA Red'] || '#A1001A',
        color: 'white',
        marginRight: '5px',
        marginBottom: '5px',
        padding: '10px 10px',
        borderRadius: '0',
        fontWeight: '600',
        fontSize: '20px',
        textDecoration: 'none',
        textTransform: 'Uppercase',
        lineHeight: '1.75em',
        display: 'flex',
        alignItems: 'center',
        transition: 'all 0.3s',
        width: '100%',
        justifyContent: 'center',
        textAlign: 'center',
        flexWrap: 'wrap',
        '&:hover': {
            backgroundColor: `${theme.palette.cms?.primary_hover}`,
        },
    },
    codeCopiedMsg: {
        color: theme?.palette?.cms?.success || '#70b603',
    },
    ieCodeBox: {
        fontSize: '22px',
    },
    ieCode: {
        color: theme?.palette?.cms?.success || '#70b603',
        fontWeight: '700',
    },
    restrictionsShown: (panelBackgroundColor) => ({
        maxWidth: '940px',
        margin: '0 auto',
        fontSize: '13px',
        padding: '5px 10px',
        backgroundColor: panelBackgroundColor?.color || theme?.palette?.cms?.['Light Beige'] || '#F8F8F0',
        '& p': {
            marginTop: '0',
        },
        /* unshrink, then fade in */
        transition: 'font-size .25s ease, margin .25s ease, padding .25s ease, opacity .5s  ease .25s',
    }),
    restrictionsHidden: (panelBackgroundColor) => ({
        fontSize: '0',
        margin: '0 auto',
        padding: '0 10px',
        opacity: '0',
        maxWidth: '940px',
        backgroundColor: panelBackgroundColor?.color || theme?.palette?.cms?.['Light Beige'] || '#F8F8F0',
        '& p': {
            marginTop: '0',
        },
        /* fade out, then shrink */
        transition: 'opacity ease .25s, fontSize .5s ease .25s, margin .5s ease .25s, padding .5s ease .25s',
    }),
}));

const CouponPageUIMobile = ({ data, panelBackgroundColor, trackEvent }) => {
    const classes = useStyles(panelBackgroundColor);
    const couponTemplate = (couponData) => {
        if (couponData) {
            const [showDetails, setShowDetails] = React.useState(false);
            const onToggleDetails = () => setShowDetails(!showDetails);
            const [codeCopied, setCodeCopied] = React.useState(false);
            const copyCode = () => {
                if (navigator?.clipboard) {
                    navigator.clipboard.writeText(couponData.promotion_code);
                    return true;
                }
                return false;
            };
            const trackFunc = (action, label) => {
                trackEvent({
                    eventCategory: 'Coupon Page',
                    eventAction: action || 'click',
                    eventLabel: label || 'coupon page element',
                });
            };

            const toggleDetails = (code) => {
                onToggleDetails();
                if (!showDetails) {
                    trackFunc('View', `Code Details for ${code}`);
                } else {
                    trackFunc('Hide', `Code Details for ${code}`);
                }
            };

            const codeCopy = (code) => {
                copyCode();
                setCodeCopied(code);
                trackFunc('Code Copy', code);
            };

            return (
                <div className={classes.couponArea} key={couponData.title}>
                    <div className={classes.couponSingle}>
                        <div className={classes.iconAndHeadingContainer}>
                            <div className={classes.iconContainer}>
                                <img className={classes.icon} src={couponData.coupon_page?.coupon_icon?.url} alt="coupon-icon" />
                            </div>
                            <div className={classes.headingContainer}>
                                {
                                    couponData.coupon_page.heading && (
                                        <ReactMarkdown className={classes.couponHeading} escapeHtml={false} source={couponData.coupon_page?.heading} />
                                    )
                                }
                            </div>
                        </div>
                        <div className={classes.copyContainer}>
                            { couponData.coupon_page.promo_copy ? <ReactMarkdown className={classes.promoCopy} escapeHtml={false} source={couponData.coupon_page?.promo_copy} /> : <div className={classes.deets_filler} />}
                        </div>
                        { couponData.promotion_details && (
                            <div
                                className={classes.detailsLink}
                                role="button"
                                onClick={() => toggleDetails(couponData.promotion_code)}
                                onKeyDown={() => toggleDetails(couponData.promotion_code)}
                                tabIndex={0}
                            >
                                {showDetails ? 'Hide Terms' : 'Restrictions Apply'}
                            </div>
                        )}
                        <div className={classes.ctaContainer}>
                            {/* internal CTA */}
                            { ((couponData.url && !couponData.promotion_code) || (couponData.promotion_code && codeCopied)) && (
                                <Link to={couponData.url} color="primary" className={classes.ctaBtn} onClick={() => { trackFunc('Shop Now CTA', couponData.url); }}>
                                    {(couponData.promotion_code && codeCopied) ? 'Shop Now' : couponData.coupon_page.cta_text}
                                </Link>
                            )}

                            {/* external Link */}
                            { couponData.external_link && (
                                <a href={couponData.external_link} color="primary" className={classes.ctaBtn} onClick={trackFunc('Shop Now CTA - External', couponData.url)}>
                                    {couponData.coupon_page.cta_text}
                                </a>
                            )}

                            {/* coupon code */}
                            { (couponData.promotion_code && !codeCopied) && (
                                <Button
                                    color="primary"
                                    className={classes.ctaBtn}
                                    onClick={() => { codeCopy(couponData.promotion_code); }}
                                    data-testid="copyCodeBtn"
                                >
                                    {couponData.coupon_page.cta_text}
                                </Button>
                            )}
                            <div className={classes.codeCopiedMsg}>
                                {codeCopied && <span data-testid="codeCopiedConfirmation">Code &quot;{couponData.promotion_code}&quot; Copied!</span>}
                            </div>
                        </div>
                    </div>
                    <div className={showDetails ? classes.restrictionsShown : classes.restrictionsHidden}>
                        { showDetails && <ReactMarkdown>{couponData.promotion_details}</ReactMarkdown> }
                    </div>
                </div>
            );
        }
        return <></>;
    };

    const limitedTimeCoupons = data.coupons.tempPromos.map((coup) => couponTemplate(coup));
    const everydayDeals = data.coupons.persistentPromos.map((coup) => couponTemplate(coup));

    if (!data || !data.coupons.tempPromos || !Array.isArray(data.coupons.tempPromos) || !data.coupons.persistentPromos || !Array.isArray(data.coupons.persistentPromos)) {
        return null;
    }

    return (
        <div>
            {limitedTimeCoupons.length > 0 && <h2 className={classes.sectionHeader}>Limited Time Offers</h2>}
            {limitedTimeCoupons}
            {everydayDeals.length > 0 && <h2 className={classes.sectionHeader}>Everyday Deals</h2>}
            {everydayDeals}

            {/* No Coupons Fallback */}
            {limitedTimeCoupons.length === 0 && everydayDeals.length === 0
                && <div className={classes.noCoupons} xs={12} md={12} item>We have no coupons at this time.<br />Please check back later.</div>}
        </div>
    );
};

CouponPageUIMobile.propTypes = {
    data: object.isRequired,
    panelBackgroundColor: object.isRequired,
    trackEvent: func.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
    trackEvent: bindActionCreators(track, dispatch),
});

export default connect(
    null,
    mapDispatchToProps,
)(CouponPageUIMobile);
